import clsx from 'clsx';
import { HTMLAttributes, ReactNode, useCallback, useMemo } from 'react';
import type { VirtualItem } from '@tanstack/react-virtual';

import { VirtualList } from '@/components/virtual-list';
import { Divider } from '@flintmint/web-shared/components/divider';

import './smart-list.scss';

const PLACEHOLDER_ROW_COUNT = 35;

export const SmartList = <T extends { id: string }>(props: SmartListProps<T>) => {
  const {
    data,
    loading,
    overscan,
    resetKey,
    activeId,
    rowProps,
    className,
    lanes = 1,
    paddingEnd,
    selections,
    getItemKey,
    rowRenderer,
    defaultSize,
    laneGap = 0,
    paddingStart,
    scrollToById,
    endReachRows,
    multiSelectionOn,
    onLastItemVisible,
    placeholderRenderer,
    customDividers = true,
    disableRowStates = false,
  } = props;

  const rows = useMemo<SmartListRow<T>[]>(() => {
    if (!customDividers) {
      return data.map(toItemRow);
    }

    const content: SmartListRow<T>[] = loading
      ? Array.from({ length: PLACEHOLDER_ROW_COUNT }, () => ({ kind: 'placeholder' }))
      : data.map(toItemRow);

    return content.flatMap((row) => [{ kind: 'divider' }, row]);
  }, [data, customDividers, loading]);

  const getRowKey = useCallback(
    (row: SmartListRow<T>) => (row.kind === 'item' && getItemKey ? getItemKey(row.item) : row.kind),
    [getItemKey]
  );

  const renderRow = useCallback(
    (row: SmartListRow<T>, virtualItem: VirtualItem<any>) => {
      const lane = virtualItem.lane ?? 0;
      const laneWidthPercent = 100 / lanes;
      const laneWidthPxLoss = ((lanes - 1) * laneGap) / lanes;
      const laneLeftPxOffset = (lane * laneGap) / lanes;
      const isDivider = row.kind === 'divider';
      const isSelected = row.kind === 'item' && selections.some((el) => el.id === row.item.id);
      const isActive = row.kind === 'item' && !!activeId && activeId === row.item.id;

      const { className: receivedClassName = '', ...rest } = row.kind === 'item' ? rowProps(row.item, isSelected) : {};
      const content =
        row.kind === 'divider' ? (
          <Divider orientation="horizontal" size="100%" />
        ) : row.kind === 'placeholder' ? (
          placeholderRenderer?.()
        ) : (
          rowRenderer(row.item, isSelected)
        );

      return (
        <li
          key={virtualItem.key}
          data-index={virtualItem.index}
          ref={virtualItem.measureElement}
          style={{
            width: lanes > 1 ? `calc(${laneWidthPercent}% - ${laneWidthPxLoss}px)` : '100%',
            left: lanes > 1 ? `calc(${lane * laneWidthPercent}% + ${laneLeftPxOffset}px)` : 0,
            transform: `translateY(${virtualItem.start}px)`,
          }}
        >
          <ul
            className={clsx(className, receivedClassName, {
              'virtual-row': !isDivider,
              'interactive-row': !isDivider && !disableRowStates,
              'divider-holder': isDivider,
              selected: !multiSelectionOn && isSelected,
              active: !multiSelectionOn && isActive,
            })}
            {...rest}
          >
            {content}
          </ul>
        </li>
      );
    },
    [
      selections,
      rowProps,
      rowRenderer,
      placeholderRenderer,
      multiSelectionOn,
      activeId,
      className,
      disableRowStates,
      lanes,
      laneGap,
    ]
  );

  return (
    <VirtualList<SmartListRow<T>>
      data={rows}
      lanes={lanes}
      loading={loading}
      overscan={overscan}
      resetKey={resetKey}
      renderer={renderRow}
      data-loaded={!loading}
      paddingEnd={paddingEnd}
      defaultSize={defaultSize}
      paddingStart={paddingStart}
      scrollToById={scrollToById}
      endReachRows={endReachRows}
      onLastItemVisible={onLastItemVisible}
      getItemKey={customDividers || !getItemKey ? undefined : getRowKey}
    />
  );
};

interface SmartListProps<T extends { id: string }> {
  data: T[];
  activeId?: string;
  loading: boolean;
  lanes?: number;
  laneGap?: number;
  overscan?: number;
  resetKey?: string;
  className?: string;
  paddingEnd?: number;
  defaultSize: number;
  scrollToById?: string;
  paddingStart?: number;
  customDividers?: boolean;
  disableRowStates?: boolean;
  multiSelectionOn?: boolean;
  selections: Array<{ id: string }>;
  endReachRows?: number;
  onLastItemVisible?: (visible: boolean) => void;
  getItemKey?: (item: T) => string;
  rowProps: (row: T, selected: boolean) => HTMLAttributes<HTMLUListElement>;
  rowRenderer: (data: T, selected?: boolean, index?: number) => ReactNode;
  /** Rendered in place of each item while `loading` (only when `customDividers` is on). */
  placeholderRenderer?: () => ReactNode;
}

// `id` lets VirtualList's scrollToById find item rows; the other kinds carry none.
type SmartListRow<T extends { id: string }> =
  | { kind: 'item'; id: string; item: T }
  | { kind: 'divider'; id?: undefined }
  | { kind: 'placeholder'; id?: undefined };

const toItemRow = <T extends { id: string }>(item: T): SmartListRow<T> => ({ kind: 'item', id: item.id, item });
